import { getMinimalSupabase } from './supabase-minimal'

// Type definition for compatibility
type SupabaseClient = any

// Get environment variables with fallback for production builds
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || ''
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || ''

// Only log in development for security
if (import.meta.env.DEV) {
  // console.log('Development environment - minimal client')
  // console.log('Supabase URL available:', !!supabaseUrl)
  // console.log('Supabase Anon Key available:', !!supabaseAnonKey)
}

if (!supabaseUrl || !supabaseAnonKey) {
  if (import.meta.env.DEV) {
    console.error('Missing Supabase environment variables:', {
      url: !!supabaseUrl,
      key: !!supabaseAnonKey
    })
  }
}

// Custom storage adapter that respects remember me preference
const createAuthStorage = () => {
  const rememberMe = window.localStorage.getItem('supabase.auth.rememberMe') === 'true'
  
  if (!rememberMe) {
    // Use session storage for non-remembered sessions
    return {
      getItem: (key: string) => {
        return window.sessionStorage.getItem(key)
      },
      setItem: (key: string, value: string) => {
        window.sessionStorage.setItem(key, value)
      },
      removeItem: (key: string) => {
        window.sessionStorage.removeItem(key)
      }
    }
  }
  
  // Use local storage for remembered sessions (default behavior)
  return {
    getItem: (key: string) => {
      return window.localStorage.getItem(key)
    },
    setItem: (key: string, value: string) => {
      window.localStorage.setItem(key, value)
    },
    removeItem: (key: string) => {
      window.localStorage.removeItem(key)
    }
  }
}

// Create a Supabase client with dynamic storage
export const createSupabaseClient = (rememberMe?: boolean) => {
  if (rememberMe !== undefined) {
    window.localStorage.setItem('supabase.auth.rememberMe', rememberMe.toString())
  }
  
  const authOptions: any = {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true
  }
  
  // Only add storage if we're in a browser environment
  if (typeof window !== 'undefined') {
    authOptions.storage = createAuthStorage()
  }
  
  return createClient(supabaseUrl, supabaseAnonKey, {
    auth: authOptions
  })
}

// Create storage object that safely handles SSR and client environments
const createDefaultStorage = () => {
  // Check if we're in a browser environment
  if (typeof window === 'undefined') {
    // Return a no-op storage for SSR
    return {
      getItem: () => null,
      setItem: () => {},
      removeItem: () => {}
    }
  }
  
  return {
    getItem: (key: string) => {
      try {
        return window.localStorage.getItem(key)
      } catch (error) {
        // Error accessing localStorage
        return null
      }
    },
    setItem: (key: string, value: string) => {
      try {
        window.localStorage.setItem(key, value)
      } catch (error) {
        // Error writing to localStorage
      }
    },
    removeItem: (key: string) => {
      try {
        window.localStorage.removeItem(key)
      } catch (error) {
        // Error removing from localStorage
      }
    }
  }
}

// Always use minimal client to avoid initialization issues
const supabase = getMinimalSupabase()

export { supabase }